import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const TermsOfService = () => {
  const lastUpdated = 'February 13, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/" className="legal-back-link">← Back to Home</Link>
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-subtitle">Last updated: {lastUpdated}</p>
        </div>

        <div className="legal-content">

          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using Neoshift ETL ("Service"), operated by PT Neo Teknologi Perkasa ("Neoshift," "we," "us," or "our"),
              you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an organization,
              you represent that you have the authority to bind that organization to these Terms. If you do not agree to these Terms,
              you may not use the Service.
            </p>
            <p>
              <strong>Legal Entity:</strong> PT Neo Teknologi Perkasa
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              Neoshift ETL is an AI-powered business operations automation platform that extracts data from
              documents, validates it against your business rules, and synchronizes it across your enterprise
              systems. The Service includes:
            </p>
            <ul>
              <li>AI-powered document reading and data extraction</li>
              <li>Automated data validation against business rules and records</li>
              <li>Multi-system data synchronization (ERP, CRM, WMS, and other systems)</li>
              <li>B2B operations automation (PO, invoice, delivery, and payment processing)</li>
              <li>Custom workflow development and integration</li>
              <li>Ongoing support and maintenance</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Enterprise Agreements</h2>
            <p>
              The Service is provided under enterprise agreements tailored to each client's requirements.
              By engaging our services, you agree to:
            </p>
            <ul>
              <li>Provide accurate company and contact information</li>
              <li>Designate authorized personnel to manage the Service</li>
              <li>Maintain the confidentiality of any system access credentials</li>
              <li>Cooperate with our onboarding and integration process</li>
              <li>Notify us promptly of any unauthorized access or security concerns</li>
            </ul>
            <p>
              Specific service scope, deliverables, timelines, and service level agreements (SLAs)
              are defined in your individual enterprise agreement or statement of work.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Fees and Payment</h2>
            <p>
              Fees for the Service are determined by your enterprise agreement and may include setup fees,
              recurring service fees, and usage-based charges. By entering into an agreement, you agree to:
            </p>
            <ul>
              <li>Pay all fees as specified in your enterprise agreement</li>
              <li>Provide valid billing information</li>
              <li>Pay invoices within the agreed payment terms</li>
            </ul>
            <p>
              We reserve the right to modify pricing for renewal periods with 30 days advance written notice.
            </p>

            <h3>4.1 No Refunds</h3>
            <p>
              All fees paid under your enterprise agreement are non-refundable. This includes setup fees,
              service fees, custom development charges, and any other amounts paid for the Service. If either
              party terminates the agreement, you remain responsible for all fees incurred up to and including
              the effective date of termination. No credits or refunds will be issued for partial service
              periods or unused capacity.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. AI-Powered Features</h2>
            <p>
              The Service includes AI-powered features for document extraction, data validation, and workflow
              automation. You acknowledge and agree to the following:
            </p>

            <h3>5.1 Accuracy and Limitations</h3>
            <p>
              While our AI achieves high accuracy rates, AI-powered extraction and validation may occasionally
              contain errors or inaccuracies. Artificial intelligence models can:
            </p>
            <ul>
              <li>Misread or misinterpret document content, especially with poor-quality scans or non-standard layouts</li>
              <li>Produce incorrect data extraction results that require human review</li>
              <li>Fail to identify all validation discrepancies</li>
              <li>Require retraining when document formats change significantly</li>
            </ul>

            <h3>5.2 Human Review</h3>
            <p>
              The Service is designed with human-in-the-loop capabilities. We recommend maintaining
              human review checkpoints, particularly during initial deployment. Our system flags
              low-confidence extractions for manual verification to ensure data quality.
            </p>

            <h3>5.3 Your Responsibility</h3>
            <p>You are solely responsible for:</p>
            <ul>
              <li>Verifying the accuracy of extracted and validated data before acting on it</li>
              <li>Maintaining appropriate human oversight of automated processes</li>
              <li>Ensuring the correctness of business rules provided for validation</li>
              <li>Any consequences resulting from data processed by the Service</li>
            </ul>

            <h3>5.4 No Liability for Processing Errors</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEOSHIFT DISCLAIMS ALL LIABILITY FOR ANY LOSSES, DAMAGES,
              OR CONSEQUENCES ARISING FROM ERRORS IN AI-POWERED DATA EXTRACTION, VALIDATION, OR PROCESSING,
              INCLUDING BUT NOT LIMITED TO BUSINESS LOSSES, REVENUE IMPACTS, INVENTORY DISCREPANCIES, OR ANY
              OTHER DAMAGES. WE MAKE NO GUARANTEES REGARDING 100% ACCURACY OF AUTOMATED PROCESSING.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Submit documents containing malicious code or harmful data</li>
              <li>Attempt to gain unauthorized access to our systems or other clients' data</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Use the Service to compete with or create a similar service</li>
              <li>Share system access credentials or allow unauthorized access</li>
              <li>Exceed the scope of use defined in your enterprise agreement</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Data</h2>
            <p>
              You retain all rights to the documents and data you submit to the Service ("Your Data").
              By using the Service, you grant us a limited license to process Your Data solely to provide
              the Service. We will not sell, share, or use Your Data for purposes other than providing
              the Service without your consent.
            </p>
            <p>
              You are responsible for ensuring that you have the right to submit Your Data for processing
              and that doing so does not violate any third-party rights or applicable laws. You must ensure
              that any personal data contained within submitted documents is processed in compliance with
              applicable data protection laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Security</h2>
            <p>We implement industry-standard security measures to protect Your Data, including:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Client data isolation and access controls</li>
              <li>Regular security monitoring and auditing</li>
              <li>Secure document processing pipelines</li>
              <li>Encrypted system integration connections</li>
            </ul>
            <p>
              However, no system is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Intellectual Property</h2>
            <p>
              The Service, including its AI models, processing algorithms, workflow architecture, code, and
              documentation, is owned by Neoshift and protected by intellectual property laws. Custom
              configurations and workflows developed specifically for your use case remain our intellectual
              property unless otherwise specified in your enterprise agreement.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Third-Party Services</h2>
            <p>
              The Service integrates with third-party systems including your ERP, CRM, WMS, cloud
              storage, and other enterprise platforms. Your use of these third-party systems is subject
              to their respective terms and policies. We are not responsible for the actions, availability,
              or policies of third-party services.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Service Availability</h2>
            <p>
              We strive to maintain high availability but do not guarantee uninterrupted access to the Service.
              We may perform scheduled maintenance with advance notice when possible. Service level
              commitments, where applicable, are defined in your enterprise agreement. We are not liable for any
              downtime or issues resulting from factors beyond our reasonable control.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEOSHIFT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES,
              ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR
              THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED, OR THAT
              AI-POWERED PROCESSING WILL ACHIEVE ANY SPECIFIC ACCURACY RATE.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Termination</h2>
            <p>
              Either party may terminate the enterprise agreement as specified in its terms. Upon termination:
            </p>
            <ul>
              <li>Your access to the Service will be suspended</li>
              <li>You may request export of Your Data within 30 days</li>
              <li>After 30 days, Your Data may be permanently deleted</li>
              <li>All outstanding fees remain payable and are non-refundable</li>
              <li>Custom integrations and workflows will be decommissioned</li>
            </ul>
            <p>
              We may suspend or terminate your access immediately if you materially breach these Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Confidentiality</h2>
            <p>
              Both parties agree to maintain the confidentiality of proprietary information disclosed during
              the engagement. This includes, but is not limited to, business processes, system configurations,
              pricing, and any data processed through the Service. Confidentiality obligations survive
              termination of the agreement.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes via
              written notice to your designated contact. Your continued use of the Service after changes
              take effect constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>17. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which Neoshift is incorporated,
              without regard to conflict of law principles. Any disputes shall be resolved through binding
              arbitration or in the courts of that jurisdiction.
            </p>
          </section>

          <section className="legal-section">
            <h2>18. Contact Information</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p className="legal-contact">
              <strong>Email:</strong> <a href="mailto:support@bi.neoshift.ai">support@bi.neoshift.ai</a>
            </p>
          </section>

        </div>

        <div className="legal-footer">
          <p>&copy; {new Date().getFullYear()} Neoshift. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
